import type { NotificationView } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { getUnreadNotificationCount, listNotifications, markNotificationRead } from '../notifications.api';

/** There is no push channel to the browser yet, so the unread badge polls; the list itself loads only when the bell is opened. */
export const UNREAD_POLL_INTERVAL_MS = 30_000;
const LIST_LIMIT = 20;

export type NotificationListState = { status: 'idle' } | { status: 'loading' } | { status: 'loaded'; items: NotificationView[] } | { status: 'error' };

export function useNotifications() {
  const [unread, setUnread] = useState(0);
  const [listState, setListState] = useState<NotificationListState>({ status: 'idle' });

  useEffect(() => {
    let cancelled = false;
    const refreshCount = () => {
      getUnreadNotificationCount()
        .then((count) => {
          if (!cancelled) setUnread(count);
        })
        .catch(() => undefined);
    };

    refreshCount();
    const timer = setInterval(refreshCount, UNREAD_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const load = useCallback(async () => {
    setListState((state) => (state.status === 'loaded' ? state : { status: 'loading' }));
    try {
      const [items, count] = await Promise.all([listNotifications(LIST_LIMIT), getUnreadNotificationCount()]);
      setListState({ status: 'loaded', items });
      setUnread(count);
    } catch {
      setListState({ status: 'error' });
    }
  }, []);

  /** Marks one read on the server first, so the badge never shows a count the server hasn't agreed to. */
  async function markRead(notification: NotificationView): Promise<void> {
    if (notification.readAt) return;
    await markNotificationRead(notification.id);
    const readAt = new Date().toISOString();
    setListState((state) =>
      state.status === 'loaded' ? { status: 'loaded', items: state.items.map((item) => (item.id === notification.id ? { ...item, readAt } : item)) } : state,
    );
    setUnread((count) => Math.max(0, count - 1));
  }

  return { unread, listState, load, markRead };
}
